// WSR API Service
// Handles all WSR-related API calls to both React and .NET backends

import WSRFileStorageService from './wsrFileStorage';

interface WSRCreateRequest {
  client_id: number;
  week_ending: string;
  reporting_period: string;
  reporting_date: string;
  project_sponsor: string;
  prepared_by: string;
  phone: string;
  email: string;
  report_summary: string;
  conclusions_recommendations: string;
  status: 'draft' | 'submitted' | 'approved';
  original_file_name?: string;
  original_file_type?: string;
  original_file_size?: number;
  work_items: Array<{
    category: 'accomplished' | 'targets' | 'risks';
    task?: string;
    risk?: string;
    notes: string;
    priority: 'high' | 'medium' | 'low';
    status: 'active' | 'completed' | 'cancelled';
    assigned_to?: string;
    due_date?: string;
  }>;
  metrics: Array<{
    metric_name: string;
    current_value: string;
    target_value: string;
    status: 'on-track' | 'at-risk' | 'behind';
    trend: 'improving' | 'stable' | 'declining';
    unit: string;
    notes: string;
  }>;
}

class WSRApiService {
  private static instance: WSRApiService;
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:4500';
  }

  static getInstance(): WSRApiService {
    if (!WSRApiService.instance) {
      WSRApiService.instance = new WSRApiService();
    }
    return WSRApiService.instance;
  }

  // Get WSRs for a client
  async getWSRs(clientId: number, limit: number = 10) {
    const response = await fetch(`${this.baseUrl}/api/clients/${clientId}/wsrs?limit=${limit}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch WSRs: ${response.statusText}`);
    }
    return response.json();
  }

  // Get specific WSR
  async getWSR(wsrId: number) {
    const response = await fetch(`${this.baseUrl}/api/wsrs/${wsrId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch WSR: ${response.statusText}`);
    }
    return response.json();
  }

  // Create new WSR
  async createWSR(wsrData: any) {
    // Transform the data to match API expectations
    const apiData = this.transformWSRForAPI(wsrData);
    
    const response = await fetch(`${this.baseUrl}/api/wsrs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(apiData),
    });

    if (!response.ok) {
      throw new Error(`Failed to create WSR: ${response.statusText}`);
    }

    const result = await response.json();
    
    // Create work items and metrics separately
    if (result.id) {
      await this.saveWorkItems(result.id, wsrData);
      await this.saveMetrics(result.id, wsrData);
      
      // Generate and upload published PDF if WSR is being submitted/approved
      if (wsrData.status === 'submitted' || wsrData.status === 'approved') {
        await this.generatePublishedPDF(result.id, wsrData);
      }
    }

    return result;
  }

  // Update existing WSR
  async updateWSR(wsrId: number, wsrData: any) {
    const apiData = this.transformWSRForAPI(wsrData);
    
    const response = await fetch(`${this.baseUrl}/api/wsrs/${wsrId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(apiData),
    });

    if (!response.ok) {
      throw new Error(`Failed to update WSR: ${response.statusText}`);
    }

    const result = await response.json();
    
    // Update work items and metrics
    await this.saveWorkItems(wsrId, wsrData);
    await this.saveMetrics(wsrId, wsrData);

    return result;
  }

  // Delete WSR
  async deleteWSR(wsrId: number) {
    const response = await fetch(`${this.baseUrl}/api/wsrs/${wsrId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete WSR: ${response.statusText}`);
    }

    return response.json();
  }

  // Save work items for a WSR
  private async saveWorkItems(wsrId: number, wsrData: any) {
    // Delete existing work items
    await fetch(`${this.baseUrl}/api/wsrs/${wsrId}/work-items`, {
      method: 'DELETE',
    });

    // Create new work items
    const allWorkItems = [
      ...wsrData.accomplished.map((item: any) => ({ ...item, category: 'accomplished' })),
      ...wsrData.targets.map((item: any) => ({ ...item, category: 'targets' })),
      ...wsrData.risks.map((item: any) => ({ ...item, category: 'risks' }))
    ];

    for (const item of allWorkItems) {
      await fetch(`${this.baseUrl}/api/wsrs/${wsrId}/work-items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category: item.category,
          task: item.task,
          risk: item.risk,
          notes: item.notes,
          priority: item.priority,
          status: item.status,
          assigned_to: item.assigned_to,
          due_date: item.due_date
        }),
      });
    }
  }

  // Save metrics for a WSR
  private async saveMetrics(wsrId: number, wsrData: any) {
    // Delete existing metrics
    await fetch(`${this.baseUrl}/api/wsrs/${wsrId}/metrics`, {
      method: 'DELETE',
    });

    // Create new metrics
    for (const metric of wsrData.metrics) {
      await fetch(`${this.baseUrl}/api/wsrs/${wsrId}/metrics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          metric_name: metric.metric_name,
          current_value: metric.current_value,
          target_value: metric.target_value,
          status: metric.status,
          trend: metric.trend,
          unit: metric.unit,
          notes: metric.notes
        }),
      });
    }
  }

  // Transform WSR data for API
  private transformWSRForAPI(wsrData: any): WSRCreateRequest {
    return {
      client_id: wsrData.client_id,
      week_ending: wsrData.week_ending,
      reporting_period: wsrData.reporting_period,
      reporting_date: wsrData.reporting_date,
      project_sponsor: wsrData.project_sponsor,
      prepared_by: wsrData.prepared_by,
      phone: wsrData.phone,
      email: wsrData.email,
      report_summary: wsrData.report_summary,
      conclusions_recommendations: wsrData.conclusions_recommendations,
      status: wsrData.status,
      work_items: [],
      metrics: [],
      original_file_name: wsrData.originalFileName,
      original_file_type: wsrData.originalFileType,
      original_file_size: wsrData.originalFileSize
    };
  }

  // Get WSR templates
  async getWSRTemplates(clientId?: number) {
    const url = clientId 
      ? `${this.baseUrl}/api/wsr-templates?client_id=${clientId}`
      : `${this.baseUrl}/api/wsr-templates`;
      
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch WSR templates: ${response.statusText}`);
    }
    return response.json();
  }

  // Create WSR from template
  async createWSRFromTemplate(templateId: number, weekEnding: string) {
    const response = await fetch(`${this.baseUrl}/api/wsr-templates/${templateId}/create-wsr`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ week_ending: weekEnding }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create WSR from template: ${response.statusText}`);
    }

    return response.json();
  }

  // Get WSR analytics/summary
  async getWSRAnalytics(clientId: number, startDate?: string, endDate?: string) {
    let url = `${this.baseUrl}/api/clients/${clientId}/wsr-analytics`;
    const params = new URLSearchParams();
    
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch WSR analytics: ${response.statusText}`);
    }
    return response.json();
  }

  // Generate and upload published PDF using Supabase Storage
  private async generatePublishedPDF(wsrId: number, wsrData: any): Promise<void> {
    try {
      const fileStorage = WSRFileStorageService.getInstance();
      const pdfResult = await fileStorage.generateAndUploadPDF(wsrData, wsrData.client_id);
      
      // Update WSR record with published PDF URL
      await fetch(`${this.baseUrl}/api/wsrs/${wsrId}/published-pdf`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          published_pdf_url: pdfResult.fileUrl,
          published_pdf_path: pdfResult.filePath
        }),
      });
      
      console.log('Published PDF generated and uploaded:', pdfResult.fileUrl);
    } catch (error) {
      console.error('Failed to generate published PDF:', error);
      // Don't throw error - PDF generation is optional
    }
  }
}

export default WSRApiService;